import { makeAutoObservable, runInAction } from "mobx";
import type { RootStore } from "@intrasync/stores";

interface DriverLicense {
  licenseNumber: string;
  licenseType: "regular" | "cdl_a" | "cdl_b" | "cdl_c" | "motorcycle" | "endorsement";
  state: string;
  expirationDate: string;
  restrictions: string;
}

interface Certification {
  certificationId: string;
  certificationName: string;
  issuingOrganization: string;
  issueDate: string;
  expirationDate: string;
  renewalRequired: boolean;
  documentUrl?: string;
}

interface VehicleAssignment {
  vehicleId: string;
  assignmentType: "primary" | "secondary" | "temporary";
  startDate: string;
  endDate?: string;
  notes: string;
}

interface FleetDriverData {
  driverId: string;
  employeeId: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  dateOfBirth: string;
  hireDate: string;
  status: "active" | "inactive" | "suspended" | "terminated";
  licenses: DriverLicense[];
  certifications: Certification[];
  vehicleAssignments: VehicleAssignment[];
  medicalCertification?: {
    certificateNumber: string;
    expirationDate: string;
    restrictions: string;
  };
  backgroundCheck: {
    completed: boolean;
    completionDate?: string;
    status: "pending" | "passed" | "failed" | "expired";
    nextDueDate?: string;
  };
  trainingRecords: {
    trainingId: string;
    trainingName: string;
    completionDate: string;
    instructorName: string;
    score?: number;
    renewalDate?: string;
  }[];
  emergencyContact: {
    name: string;
    relationship: string;
    phone: string;
    address: string;
  };
  notes: string;
}

class FleetDriverManagementWizardStore {
  rootStore: RootStore;

  // UI State
  activeStep = 0;
  loading = false;
  error: string | null = null;
  success: string | null = null;

  // Form Data
  formData: FleetDriverData = {
    driverId: "",
    employeeId: "",
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    dateOfBirth: "",
    hireDate: new Date().toISOString().split("T")[0],
    status: "active",
    licenses: [],
    certifications: [],
    vehicleAssignments: [],
    backgroundCheck: {
      completed: false,
      status: "pending",
    },
    trainingRecords: [],
    emergencyContact: {
      name: "",
      relationship: "",
      phone: "",
      address: "",
    },
    notes: "",
  };

  // Lookup Data
  vehicles: any[] = [];
  employees: any[] = [];
  certificationTypes: any[] = [];
  trainingPrograms: any[] = [];

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
    this.formData.driverId = this.generateDriverId();
  }

  // Actions
  setActiveStep = (step: number) => {
    this.activeStep = step;
  };

  nextStep = () => {
    if (this.validateCurrentStep()) {
      this.activeStep++;
    }
  };

  prevStep = () => {
    if (this.activeStep > 0) {
      this.activeStep--;
    }
  };

  updateFormData = <K extends keyof FleetDriverData>(
    field: K,
    value: FleetDriverData[K]
  ) => {
    this.formData[field] = value;
  };

  addLicense = (license: DriverLicense) => {
    this.formData.licenses.push(license);
  };

  removeLicense = (index: number) => {
    this.formData.licenses.splice(index, 1);
  };

  updateLicense = (index: number, license: DriverLicense) => {
    this.formData.licenses[index] = license;
  };

  addCertification = (certification: Certification) => {
    this.formData.certifications.push(certification);
  };

  removeCertification = (index: number) => {
    this.formData.certifications.splice(index, 1);
  };

  updateCertification = (index: number, certification: Certification) => {
    this.formData.certifications[index] = certification;
  };

  addVehicleAssignment = (assignment: VehicleAssignment) => {
    this.formData.vehicleAssignments.push(assignment);
  };

  removeVehicleAssignment = (index: number) => {
    this.formData.vehicleAssignments.splice(index, 1);
  };

  updateVehicleAssignment = (index: number, assignment: VehicleAssignment) => {
    this.formData.vehicleAssignments[index] = assignment;
  };

  addTrainingRecord = (training: any) => {
    this.formData.trainingRecords.push(training);
  };

  removeTrainingRecord = (index: number) => {
    this.formData.trainingRecords.splice(index, 1);
  };

  updateTrainingRecord = (index: number, training: any) => {
    this.formData.trainingRecords[index] = training;
  };

  generateDriverId = (): string => {
    const date = new Date();
    const year = date.getFullYear().toString().slice(-2);
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const random = Math.floor(Math.random() * 1000).toString().padStart(3, "0");
    return `DRV-${year}${month}-${random}`;
  };

  // Validation
  validateCurrentStep = (): boolean => {
    this.error = null;

    switch (this.activeStep) {
      case 0:
        if (!this.formData.firstName) {
          this.error = "Please enter the driver's first name";
          return false;
        }
        if (!this.formData.lastName) {
          this.error = "Please enter the driver's last name";
          return false;
        }
        if (!this.formData.email) {
          this.error = "Please enter a valid email address";
          return false;
        }
        if (!this.formData.phone) {
          this.error = "Please enter a phone number";
          return false;
        }
        if (!this.formData.dateOfBirth) {
          this.error = "Please enter date of birth";
          return false;
        }
        return true;

      case 1: {
        if (this.formData.licenses.length === 0) {
          this.error = "Please add at least one driver's license";
          return false;
        }

        const invalidLicenses = this.formData.licenses.some(
          (license) => !license.licenseNumber || !license.licenseType || !license.expirationDate
        );
        if (invalidLicenses) {
          this.error = "Please complete all license information";
          return false;
        }

        const expiredLicenses = this.formData.licenses.some(
          (license) => new Date(license.expirationDate) <= new Date()
        );
        if (expiredLicenses) {
          this.error = "One or more licenses are expired. Please update expiration dates.";
          return false;
        }
        return true;
      }

      case 2: {
        if (this.formData.vehicleAssignments.length === 0) {
          this.error = "Please assign at least one vehicle to the driver";
          return false;
        }

        const invalidAssignments = this.formData.vehicleAssignments.some(
          (assignment) => !assignment.vehicleId || !assignment.startDate
        );
        if (invalidAssignments) {
          this.error = "Please complete all vehicle assignment details";
          return false;
        }
        return true;
      }

      case 3:
        if (!this.formData.emergencyContact.name) {
          this.error = "Please provide emergency contact information";
          return false;
        }
        if (!this.formData.emergencyContact.phone) {
          this.error = "Please provide emergency contact phone number";
          return false;
        }
        return true;

      default:
        return true;
    }
  };

  // API Actions
  loadInitialData = async () => {
    this.loading = true;
    this.error = null;

    try {
      // Load vehicles
      if (this.rootStore.trailerStore) {
        await this.rootStore.trailerStore.find({
          query: {
            $limit: 200,
            active: true,
          },
        });
      }

      // Load employees
      if (this.rootStore.userStore) {
        await this.rootStore.userStore.find({
          query: {
            $limit: 200,
            active: true,
          },
        });
      }

      runInAction(() => {
        this.vehicles = this.rootStore.trailerStore?.entities || [];
        this.employees = this.rootStore.userStore?.entities || [];

        // Mock certification types (would come from API)
        this.certificationTypes = [
          { id: "1", name: "DOT Physical", category: "medical", renewalPeriod: 24 },
          { id: "2", name: "Hazmat Endorsement", category: "endorsement", renewalPeriod: 60 },
          { id: "3", name: "Defensive Driving", category: "safety", renewalPeriod: 36 },
          { id: "4", name: "First Aid/CPR", category: "safety", renewalPeriod: 24 },
          { id: "5", name: "Vehicle Inspection", category: "technical", renewalPeriod: 12 },
        ];

        // Mock training programs (would come from API)
        this.trainingPrograms = [
          { id: "1", name: "New Driver Orientation", category: "onboarding", duration: 8 },
          { id: "2", name: "Safety Protocols", category: "safety", duration: 4 },
          { id: "3", name: "Vehicle Maintenance Basics", category: "technical", duration: 6 },
          { id: "4", name: "Customer Service", category: "service", duration: 3 },
          { id: "5", name: "Emergency Procedures", category: "safety", duration: 2 },
        ];

        this.loading = false;
      });
    } catch (error: any) {
      runInAction(() => {
        this.error = error.message || "Failed to load initial data";
        this.loading = false;
      });
    }
  };

  submitDriverProfile = async () => {
    if (!this.validateCurrentStep()) {
      return;
    }

    this.loading = true;
    this.error = null;

    try {
      // Create driver profile record
      const driverData = {
        ...this.formData,
        createdAt: new Date().toISOString(),
        createdBy: this.rootStore.authStore?.user?.id,
      };

      // API call would go here
      await new Promise((resolve) => setTimeout(resolve, 1500)); // Simulated API call

      runInAction(() => {
        this.success = "Driver profile created successfully!";
        this.loading = false;

        // Reset form after successful submission
        setTimeout(() => {
          this.reset();
          this.rootStore.routerStore.goTo("Dashboard");
        }, 2000);
      });
    } catch (error: any) {
      runInAction(() => {
        this.error = error.message || "Failed to create driver profile";
        this.loading = false;
      });
    }
  };

  // Computed Values
  get users() {
    return this.rootStore.userStore?.entities || [];
  }

  get selectedEmployee() {
    return this.employees.find((e) => e.id === this.formData.employeeId);
  }

  get expiringLicenses() {
    const thirtyDaysFromNow = new Date();
    thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);

    return this.formData.licenses.filter(
      (license) => new Date(license.expirationDate) <= thirtyDaysFromNow
    );
  }

  get expiringCertifications() {
    const thirtyDaysFromNow = new Date();
    thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);

    return this.formData.certifications.filter(
      (cert) => new Date(cert.expirationDate) <= thirtyDaysFromNow
    );
  }

  get primaryVehicle() {
    const primary = this.formData.vehicleAssignments.find(
      (assignment) => assignment.assignmentType === "primary"
    );
    return primary ? this.vehicles.find((v) => v.id === primary.vehicleId) : null;
  }

  get completedTrainingHours() {
    return this.formData.trainingRecords.reduce((total, training) => {
      const program = this.trainingPrograms.find(p => p.id === training.trainingId);
      return total + (program?.duration || 0);
    }, 0);
  }

  get getVehicleById() {
    return (vehicleId: string) => this.vehicles.find((v) => v.id === vehicleId);
  }

  get getCertificationTypeById() {
    return (certId: string) => this.certificationTypes.find((c) => c.id === certId);
  }

  get getTrainingProgramById() {
    return (trainingId: string) => this.trainingPrograms.find((t) => t.id === trainingId);
  }

  // Reset
  reset = () => {
    this.activeStep = 0;
    this.loading = false;
    this.error = null;
    this.success = null;
    this.formData = {
      driverId: this.generateDriverId(),
      employeeId: "",
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      dateOfBirth: "",
      hireDate: new Date().toISOString().split("T")[0],
      status: "active",
      licenses: [],
      certifications: [],
      vehicleAssignments: [],
      backgroundCheck: {
        completed: false,
        status: "pending",
      },
      trainingRecords: [],
      emergencyContact: {
        name: "",
        relationship: "",
        phone: "",
        address: "",
      },
      notes: "",
    };
  };
}

export default FleetDriverManagementWizardStore;